import { network } from "../config";
import { SpecialistData } from "../types/TypeSpecialist";
import { TypeUserData } from "../types/TypeUsers";
import url from "./formApiUrl";
import {
  compareModeratorOnlyProviderEvidenceField,
  compareProviderCredentialsField,
  compareProviderProfessionalAttributesField,
  compareProviderWorkExperienceField,
} from "./providerEvidenceReview";
import {
  areValuesEqual,
  ChangeSeverity,
  compareArrayField,
  compareCertificateField,
  compareMultiLangPriceField,
  compareMultiLangTextField,
  compareNestedField,
  comparePrimitiveField,
  compareVideoField,
  FieldChange,
} from "./profileDiffUtils";

const connectionString = process.env.REACT_APP_HTTP_CONNECTION_STRING;

export type FieldComparisonType =
  | "multiLangText"
  | "multiLangPrice"
  | "video"
  | "primitive"
  | "array"
  | "nested"
  | "custom";

export interface FieldConfig {
  fieldName: string;
  label: string;
  category: "personal" | "professional" | "services";
  severity: ChangeSeverity;
  comparisonType: FieldComparisonType;
  source?: "user" | "specialist"; // Where to get the data from
  extractor: (
    publicData: { user: TypeUserData; spec: SpecialistData },
    updatedData: { user: TypeUserData; spec: SpecialistData }
  ) => {
    oldValue: any;
    newValue: any;
  };
  formatter?: (value: any) => string;
  arrayFormatter?: (arr: any[]) => string;
  nestedExtractor?: (val: any) => string;
  customComparator?: (
    publicData: { user: TypeUserData; spec: SpecialistData },
    updatedData: { user: TypeUserData; spec: SpecialistData },
    userId: string
  ) => FieldChange | null;
}

/**
 * Centralized field configuration for profile comparison
 * Add new fields here instead of writing repetitive if-statements
 */
export const PROFILE_FIELD_CONFIGS: FieldConfig[] = [
  // ========== USER DATA FIELDS ==========
  {
    fieldName: "briefDescription",
    label: "Brief Description",
    category: "personal",
    severity: "major",
    comparisonType: "multiLangText",
    source: "user",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.user.briefDescription,
      newValue: updatedData.user.briefDescription,
    }),
  },

  // ========== PERSONAL INFORMATION ==========
  {
    fieldName: "aboutMyself",
    label: "About Myself",
    category: "personal",
    severity: "major",
    comparisonType: "multiLangText",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.aboutMyself,
      newValue: updatedData.spec.aboutMyself,
    }),
  },
  {
    fieldName: "aboutTherapy",
    label: "About Therapy",
    category: "personal",
    severity: "major",
    comparisonType: "multiLangText",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.aboutTherapy,
      newValue: updatedData.spec.aboutTherapy,
    }),
  },
  {
    fieldName: "introductionVideo",
    label: "Introduction Video",
    category: "personal",
    severity: "major",
    comparisonType: "video",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.introductionVideo,
      newValue: updatedData.spec.introductionVideo,
    }),
  },

  // ========== PROFESSIONAL INFORMATION ==========
  {
    fieldName: "mainSpecializations",
    label: "Main Specializations",
    category: "professional",
    severity: "critical",
    comparisonType: "array",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.mainSpecializations,
      newValue: updatedData.spec.mainSpecializations,
    }),
    arrayFormatter: (arr) =>
      arr?.map((s) => s.specialization).join(", ") || "-",
  },
  {
    fieldName: "subSpecializations",
    label: "Sub Specializations",
    category: "professional",
    severity: "major",
    comparisonType: "array",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.subSpecializations,
      newValue: updatedData.spec.subSpecializations,
    }),
    arrayFormatter: (arr) =>
      arr?.map((s) => s.specialization).join(", ") || "-",
  },
  {
    fieldName: "price",
    label: "Price per Session",
    category: "professional",
    severity: "major",
    comparisonType: "multiLangPrice",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.price,
      newValue: updatedData.spec.price,
    }),
  },
  {
    fieldName: "preferredLanguages",
    label: "Languages",
    category: "professional",
    severity: "major",
    comparisonType: "array",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.preferredLanguages,
      newValue: updatedData.spec.preferredLanguages,
    }),
    arrayFormatter: (arr) => arr?.join(", ") || "-",
  },
  {
    fieldName: "practiceStartYear",
    label: "Practice Start Year",
    category: "professional",
    severity: "minor",
    comparisonType: "primitive",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.practiceStartYear,
      newValue: updatedData.spec.practiceStartYear,
    }),
  },
  {
    fieldName: "practiceHours",
    label: "Practice Hours",
    category: "professional",
    severity: "minor",
    comparisonType: "primitive",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.practiceHours,
      newValue: updatedData.spec.practiceHours,
    }),
  },
  {
    fieldName: "yearsOfExperience",
    label: "Years of Experience",
    category: "professional",
    severity: "major",
    comparisonType: "primitive",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.yearsOfExperience,
      newValue: updatedData.spec.yearsOfExperience,
    }),
  },
  {
    fieldName: "contributeToProfession",
    label: "Contribution to Profession",
    category: "professional",
    severity: "major",
    comparisonType: "multiLangText",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.contributeToProfession,
      newValue: updatedData.spec.contributeToProfession,
    }),
  },
  {
    fieldName: "level",
    label: "Level",
    category: "professional",
    severity: "critical",
    comparisonType: "nested",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.level,
      newValue: updatedData.spec.level,
    }),
    nestedExtractor: (val) => val?.level || "-",
  },
  {
    fieldName: "certificates",
    label: "Certificates",
    category: "professional",
    severity: "major",
    comparisonType: "array",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.certificates,
      newValue: updatedData.spec.certificates,
    }),
    arrayFormatter: (arr) =>
      arr?.length ? `${arr.length} certificate(s)` : "No certificates",
  },
  {
    fieldName: "tags",
    label: "Specialist Tags",
    category: "professional",
    severity: "major",
    comparisonType: "array",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.tags,
      newValue: updatedData.spec.tags,
    }),
    arrayFormatter: (arr) => arr?.map((t) => t.title).join(", ") || "-",
  },
  {
    fieldName: "workExperience",
    label: "Work Experience",
    category: "professional",
    severity: "major",
    comparisonType: "custom",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.workExperience,
      newValue: updatedData.spec.workExperience,
    }),
    customComparator: (publicData, updatedData) =>
      compareProviderWorkExperienceField(publicData.spec, updatedData.spec),
  },
  {
    fieldName: "providerProfessionalAttributes",
    label: "Professional Attributes",
    category: "professional",
    severity: "major",
    comparisonType: "custom",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.providerProfessionalAttributes,
      newValue: updatedData.spec.providerProfessionalAttributes,
    }),
    customComparator: (publicData, updatedData) =>
      compareProviderProfessionalAttributesField(
        publicData.spec,
        updatedData.spec
      ),
  },
  {
    fieldName: "providerCredentials",
    label: "Provider Credentials",
    category: "professional",
    severity: "major",
    comparisonType: "custom",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.providerCredentials,
      newValue: updatedData.spec.providerCredentials,
    }),
    customComparator: (publicData, updatedData) =>
      compareProviderCredentialsField(publicData.spec, updatedData.spec),
  },
  {
    fieldName: "moderatorOnlyProviderEvidence",
    label: "Moderator-only Provider Evidence",
    category: "professional",
    severity: "major",
    comparisonType: "custom",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.moderatorOnlyProviderEvidence,
      newValue: updatedData.spec.moderatorOnlyProviderEvidence,
    }),
    customComparator: (publicData, updatedData) =>
      compareModeratorOnlyProviderEvidenceField(
        publicData.spec,
        updatedData.spec
      ),
  },

  // ========== CERTIFICATE FIELDS ==========
  // NOTE: Certificate fields cannot be compared by ID arrays alone because:
  // 1. Appreciation certificates don't have IDs in SpecialistData (fetched separately)
  // 2. Changes might be to certificate content (blurred versions), not just addition/removal
  // 3. The actual comparison happens in CertificateComparisonView by fetching from API
  //
  // CertificateComparisonView fetches detailed data for changed certificate sections.
  {
    fieldName: "appreciationCertificates",
    label: "Appreciation Certificates",
    category: "professional",
    severity: "major",
    comparisonType: "custom",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.appreciationCertificates,
      newValue: updatedData.spec.appreciationCertificates,
    }),
    customComparator: (publicData, updatedData, userId) => {
      const hasChanges = !areValuesEqual(
        publicData.spec.appreciationCertificates,
        updatedData.spec.appreciationCertificates
      );
      if (hasChanges) {
        return compareCertificateField(
          publicData.spec.appreciationCertificates,
          updatedData.spec.appreciationCertificates,
          "appreciationCertificates",
          "Appreciation Certificates"
        );
      }
      return null;
    },
  },
  {
    fieldName: "educationCertificates",
    label: "Education Certificates",
    category: "professional",
    severity: "major",
    comparisonType: "custom",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.educationCertificates,
      newValue: updatedData.spec.educationCertificates,
    }),
    customComparator: (publicData, updatedData, userId) => {
      const hasChanges = !areValuesEqual(
        publicData.spec.educationCertificates,
        updatedData.spec.educationCertificates
      );

      if (hasChanges) {
        return compareCertificateField(
          publicData.spec.educationCertificates,
          updatedData.spec.educationCertificates,
          "educationCertificates",
          "Education Certificates"
        );
      }
      return null;
    },
  },
  {
    fieldName: "qualificationCertificates",
    label: "Qualification Certificates",
    category: "professional",
    severity: "major",
    comparisonType: "custom",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.qualificationCertificates,
      newValue: updatedData.spec.qualificationCertificates,
    }),
    customComparator: (publicData, updatedData, userId) => {
      const hasChanges = !areValuesEqual(
        publicData.spec.qualificationCertificates,
        updatedData.spec.qualificationCertificates
      );

      if (hasChanges) {
        return compareCertificateField(
          publicData.spec.qualificationCertificates,
          updatedData.spec.qualificationCertificates,
          "qualificationCertificates",
          "Qualification Certificates"
        );
      }
      return null;
    },
  },
  {
    fieldName: "federationCertificates",
    label: "Federation Certificates",
    category: "professional",
    severity: "major",
    comparisonType: "custom",
    source: "specialist",
    extractor: (publicData, updatedData) => ({
      oldValue: publicData.spec.federationCertificates,
      newValue: updatedData.spec.federationCertificates,
    }),
    customComparator: (publicData, updatedData, userId) => {
      const hasChanges = !areValuesEqual(
        publicData.spec.federationCertificates,
        updatedData.spec.federationCertificates
      );

      if (hasChanges) {
        return compareCertificateField(
          publicData.spec.federationCertificates,
          updatedData.spec.federationCertificates,
          "federationCertificates",
          "Federation Certificates"
        );
      }
      return null;
    },
  },
];

/**
 * Generic comparison engine that uses field configuration
 */
export const compareProfileFields = (
  publicUser: TypeUserData,
  publicSpec: SpecialistData,
  updatedUser: TypeUserData,
  updatedSpec: SpecialistData,
  userId: string
): FieldChange[] => {
  const changes: FieldChange[] = [];

  const publicData = { user: publicUser, spec: publicSpec };
  const updatedData = { user: updatedUser, spec: updatedSpec };

  for (const config of PROFILE_FIELD_CONFIGS) {
    let change: FieldChange | null = null;

    // Extract values
    const { oldValue, newValue } = config.extractor(publicData, updatedData);

    // Apply appropriate comparison based on type
    switch (config.comparisonType) {
      case "multiLangText":
        change = compareMultiLangTextField(
          oldValue,
          newValue,
          config.fieldName,
          config.label,
          config.category,
          config.severity
        );
        break;

      case "multiLangPrice":
        change = compareMultiLangPriceField(
          oldValue,
          newValue,
          config.fieldName,
          config.label,
          config.severity
        );
        break;

      case "video":
        change = compareVideoField(
          oldValue,
          newValue,
          config.fieldName,
          config.label,
          userId,
          (uid, videoId) =>
            url(
              `${connectionString}specialists/`,
              network.specialists.getSpecialistIntroVideo,
              { id: uid, videoId }
            ),
          config.severity
        );
        break;

      case "primitive":
        change = comparePrimitiveField(
          oldValue,
          newValue,
          config.fieldName,
          config.label,
          config.category,
          config.severity,
          config.formatter
        );
        break;

      case "array":
        change = compareArrayField(
          oldValue,
          newValue,
          config.fieldName,
          config.label,
          config.category,
          config.severity,
          config.arrayFormatter
        );
        break;

      case "nested":
        if (config.nestedExtractor) {
          change = compareNestedField(
            oldValue,
            newValue,
            config.fieldName,
            config.label,
            config.category,
            config.severity,
            config.nestedExtractor
          );
        }
        break;

      case "custom":
        if (config.customComparator) {
          change = config.customComparator(publicData, updatedData, userId);
        }
        break;
    }

    if (change) {
      changes.push(change);
    }
  }

  return changes;
};
